import { useEffect } from "react";
import { useConvexAuth, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { useUserStore } from "../Store/userStore";
import { setCurrentUser } from "../../../auth";
import convex, { reportConvexAuthState } from "../../../convexClient";
import { getBrowserTimeZone } from "../../../utils/timeZone";
import { normalizeUserProfile } from "../../../utils/userProfile";

/**
 * Invisible component that bridges Convex Auth state into the Zustand user store
 * and the auth compatibility shim (src/auth.js).
 *
 * Render this once inside ConvexAuthProvider (e.g. in App.js).
 */
const SyncAuthToStore = () => {
  const { isAuthenticated, isLoading } = useConvexAuth();

  // Fetch the app-level user profile reactively once authenticated.
  // "skip" prevents the query from running while unauthenticated.
  const profile = useQuery(
    api.users.getProfile,
    isAuthenticated ? {} : "skip"
  );

  useEffect(() => {
    reportConvexAuthState({ isLoading, isAuthenticated });
  }, [isAuthenticated, isLoading]);

  // Mark store as initialized once auth loading resolves.
  useEffect(() => {
    if (!isLoading) {
      useUserStore.setState({ initialized: true });
    }
  }, [isLoading]);

  // When authenticated and profile is loaded, sync to store + auth shim.
  useEffect(() => {
    if (!isAuthenticated || isLoading) return;

    if (profile === undefined) return; // still loading

    const normalizedProfile = normalizeUserProfile(profile);
    const needsProfileRepair =
      !!profile &&
      !!normalizedProfile &&
      (normalizedProfile.email !== (profile.email || "") ||
        normalizedProfile.displayName !== (profile.displayName || ""));

    // If profile exists, sync whatever data we have immediately so the UI
    // shows the real email/name while any backfill mutation runs.
    if (normalizedProfile) {
      useUserStore.getState().syncProfile(normalizedProfile);
      setCurrentUser({
        uid: normalizedProfile.uid ?? null,
        email: normalizedProfile.email,
        displayName: normalizedProfile.displayName || null,
        photoURL: normalizedProfile.photoURL || null,
      });
    }

    // Create or backfill the profile whenever uid is missing.
    // This covers: new users (profile === null) and users whose row was
    // created by Convex Auth with just {email} and no uid yet.
    if (!profile?.uid || needsProfileRepair) {
      convex
        .mutation(api.users.createOrUpdateProfile, {
          email: normalizedProfile?.email ?? "",
          displayName: normalizedProfile?.displayName ?? null,
          timeZone: getBrowserTimeZone(),
          showOnboarding: profile === null, // only true for brand-new users
          createdAt: new Date().toISOString(),
        })
        .catch((err) =>
          console.warn("[SyncAuthToStore] createOrUpdateProfile:", err)
        );
    }
  }, [isAuthenticated, isLoading, profile]);

  // When signed out, clear the store and auth shim.
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      useUserStore.getState().clearProfile();
      setCurrentUser(null);
    }
  }, [isAuthenticated, isLoading]);

  return null;
};

export default SyncAuthToStore;
